<template>
  <div :class="['relative', wrapperClass]" ref="searchRef">
    <HLInputGroup :id="`search-bar-${Math.random().toString(36).substr(2, 9)}`" :size="selectSize" class="w-full flex flex-wrap items-center gap-2 min-w-0">
      <!-- Main Search HLSelect Component -->
      <HLSelect
        id="search-bar-select"
        ref="selectRef"
        v-model:value="selectedValue"
        :options="selectOptions"
        :placeholder="placeholder"
        :filterable="true"
        :showSearchIcon="true"
        :size="selectSize"
        :option-renderer="optionRenderer"
        @update:value="handleSelectChange"
        @search="handleSearchInput"
        :show="searchQuery.trim().length > 0 && selectOptions.length > 0"
        class="flex-1 min-w-0 sm:min-w-[150px] md:min-w-[150px] lg:min-w-[180px] xl:min-w-[450px] max-w-full search-bar-select basis-0 order-1"
      >
        <!-- Custom Icon Slot -->
        <template #icon>
          <div class="hr-select-menu-placeholder-icon">
            <svg 
              :class="['text-gray-400', iconSizeClass]" 
              stroke="currentColor"
              v-bind="getIconAttributes()"
            >
              <path :d="ICONS.search" />
            </svg>
          </div>
        </template>
        
        <!-- Custom Empty Slot -->
        <template #empty>
          <div class="text-center text-gray-500 dark:text-gray-400">
            <slot name="empty" :query="searchQuery" :filter="selectedFilter">
              <svg class="w-8 h-8 mx-auto mb-2 text-gray-400" stroke="currentColor" v-bind="getIconAttributes()">
                <path :d="emptyIcon || ICONS.search" />
              </svg>
              <p class="text-sm">{{ emptyMessage }} "{{ searchQuery }}"</p>
              <p v-if="showFilterInEmptyState && selectedFilter && headerBadgeConfig?.label" class="text-xs mt-1">
                in {{ headerBadgeConfig.label }}
              </p>
            </slot>
          </div>
        </template>
        
        <!-- Custom Header Slot -->
        <template #header>
          <slot name="dropdown-header" :query="searchQuery" :count="results.length">
            <div class="px-2 py-0 border-b border-gray-200 dark:border-gray-700">
              <div class="flex items-center justify-between gap-1 min-h-[20px]">
                <div class="flex items-center gap-1">
                  <span class="text-[10px] font-medium text-gray-900 dark:text-black leading-none">{{ dropdownTitle }}</span>
                  <HLBadge 
                    v-if="headerBadgeConfig && headerBadgeConfig.label" 
                    id="search-header-badge"
                    :color="headerBadgeConfig.variant || 'blue'" 
                    size="sm"
                    class="scale-75 origin-left"
                  >
                    {{ headerBadgeConfig.label }}
                  </HLBadge>
                </div>
                <span class="text-[10px] text-gray-500 dark:text-gray-400 whitespace-nowrap leading-none">
                  ({{ results.length }} result{{ results.length !== 1 ? 's' : '' }})
                </span>
              </div>
            </div>
          </slot>
        </template>
      </HLSelect>
      
      <!-- Filter Select (if enabled) -->
      <HLSelect
        v-if="showFilter"
        :id="`search-filter-${Math.random().toString(36).substr(2, 9)}`"
        v-model:value="selectedFilter"
        :options="filterSelectOptions"
        :size="selectSize"
        @update:value="handleFilterChange"
        :title="filterLabel"
        class="search-filter-select w-full sm:w-auto sm:min-w-[100px] lg:w-[100px] flex-shrink-0 order-2 basis-full sm:basis-auto"
      />

      <!-- Custom Actions Slot -->
      <div v-if="$slots.actions" class="w-full md:w-auto flex-shrink-0 order-3 basis-full md:basis-auto">
        <slot name="actions"></slot>
      </div>
    </HLInputGroup>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted, useSlots, resolveComponent, h } from 'vue'
import { HLBadge, HLSelect, HLTag, HLInputGroup, HLButton, HLIcon, HLText } from '@platform-ui/highrise'
import { ChevronRightIcon } from '@gohighlevel/ghl-icons/24/outline'
import { ICONS, getIconAttributes } from '@/constants/icons'
import { t } from '@/i18n/en-us'
import { 
  getSearchTypeHLTag, 
  formatStatus,
  getProjectStatusHLTag,
  getFeatureStatusHLTag,
  getBugStatusHLTag,
  getTestCaseStatusHLTag,
  getTestRunStatusHLTag
} from '@/utils/badgeHelpers'
import { SEARCH_TYPE_LABELS } from '@/constants/dashboard'

const slots = useSlots()

const props = defineProps({
  // Model value
  modelValue: {
    type: String,
    default: ''
  },
  
  // Results
  results: {
    type: Array,
    default: () => []
  },
  
  // Placeholder
  placeholder: {
    type: String,
    default: () => t('searchBar.placeholder')
  },
  
  // Size
  size: {
    type: String,
    default: 'md',
    validator: (value) => ['sm', 'md', 'lg'].includes(value)
  },
  
  // Features
  showClearButton: {
    type: Boolean,
    default: true
  },
  showFilter: {
    type: Boolean,
    default: false
  },
  filterOptions: {
    type: Array,
    default: () => [
      { label: 'All', value: '' }
    ]
  },
  filterLabel: {
    type: String,
    default: () => t('searchBar.filterLabel')
  },
  
  // Dropdown
  showDropdownOnFocus: {
    type: Boolean,
    default: true
  },
  showAllResultsInitially: {
    type: Boolean,
    default: false
  },
  dropdownTitle: {
    type: String,
    default: () => t('searchBar.dropdownTitle')
  },
  emptyMessage: {
    type: String,
    default: () => t('searchBar.noResultsFound')
  },
  
  // Enhanced dropdown header (declarative badge support)
  headerBadgeConfig: {
    type: Object,
    default: null
    // Format: { label: string, variant: string }
  },
  
  // Enhanced empty state (declarative icon support)
  emptyIcon: {
    type: String,
    default: null // SVG path string
  },
  showFilterInEmptyState: {
    type: Boolean,
    default: false
  },
  
  // Result rendering (declarative component)
  resultComponent: {
    type: [Object, String],
    default: null // Component or component name
  },
  resultItemKey: {
    type: String,
    default: 'id' // Key property for v-for
  },
  
  // Debounce
  debounce: {
    type: Number,
    default: 300
  },
  
  // Loading
  loading: {
    type: Boolean,
    default: false
  },
  
  // Custom classes
  wrapperClass: {
    type: String,
    default: 'w-full'
  }
})

const emit = defineEmits([
  'update:modelValue',
  'search',
  'clear',
  'focus',
  'blur',
  'result-click',
  'filter-change'
])

// Refs
const searchRef = ref(null)
const selectRef = ref(null)
const searchQuery = ref(props.modelValue)
const selectedValue = ref(null)
const selectedFilter = ref(props.filterOptions[0]?.value || '')
const showDropdown = ref(false)
const debounceTimer = ref(null)
const isSearching = ref(false)
const isClosingDropdown = ref(false)

// Computed
const iconSizeClass = computed(() => {
  const sizes = {
    sm: 'h-3 w-3',
    md: 'h-4 w-4',
    lg: 'h-5 w-5'
  }
  return sizes[props.size] || sizes.md
})

const inputSizeClass = computed(() => {
  const sizes = {
    sm: 'text-sm py-1.5',
    md: 'text-base py-2 md:py-2.5',
    lg: 'text-lg py-3'
  }
  return sizes[props.size] || sizes.md
})

const inputPaddingClass = computed(() => {
  if (props.showFilter) {
    return 'pl-10 pr-24 md:pr-32'
  }
  if (props.showClearButton) {
    return 'pl-10 pr-10'
  }
  return 'pl-10 pr-4'
})

// Convert filterOptions to HLSelect format
const filterSelectOptions = computed(() => {
  return props.filterOptions.map(option => ({
    label: option.label,
    value: option.value
  }))
})

// Convert results to HLSelect options format
const selectOptions = computed(() => {
  if (!props.results || props.results.length === 0) {
    return []
  }
  
  return props.results.map((result, index) => {
    // Support different result formats
    const labelText = result.label || result.name || result.title || String(result.value || index)
    const value = result.value || result.id || String(index)
    const description = result.description || result.subtitle
    
    // Create custom render function for the option
    // This replaces the default option rendering with our custom layout
    const renderOption = () => h('div', { class: 'flex items-center justify-between w-full py-1' }, [
      h('div', { class: 'flex flex-col flex-1 min-w-0' }, [
        h('div', { class: 'flex items-center space-x-1.5' }, [
          h(HLText, { 
            id: `search-option-label-${result.id || index}`,
            size: 'xl', 
            weight: 'semibold',
            htmlTag: 'span',
            class: 'truncate' 
          }, {
            default: () => labelText
          }),
          result.type ? h(HLTag, {
            id: `search-type-${result.id || index}`,
            color: getSearchTypeTagColor(result.type),
            size: 'xs',
            bordered: false,
            round: true,
            class: 'flex-shrink-0'
          }, {
            default: () => getTypeLabel(result.type)
          }) : null
        ]),
        description ? h('p', { class: 'text-sm text-gray-500 dark:text-gray-400 truncate text-left' }, description) : null,
        h('div', { class: 'flex items-center space-x-3 mt-1' }, [
          result.parentInfo ? h('span', { class: 'text-xs text-gray-400 dark:text-gray-500' }, result.parentInfo) : null,
          result.status && getStatusTagConfig({ ...result, type: result.type?.toLowerCase() }) ? h(HLTag, {
            id: `search-status-${result.id || index}`,
            color: getStatusTagConfig({ ...result, type: result.type?.toLowerCase() }).color,
            size: 'xs',
            bordered: false,
            round: true
          }, {
            default: () => formatStatus(result.status)
          }) : null
        ])
      ]),
      h('div', { class: 'flex-shrink-0 ml-2' }, [
        h(HLIcon, {
          id: `search-chevron-${result.id || index}`,
          size: 14,
          color: '#9CA3AF'
        }, {
          default: () => h(ChevronRightIcon)
        })
      ])
    ])
    
    return {
      label: labelText,
      value,
      description,
      renderOption,
      // Add tag information for HLSelect to render
      tagColor: result.type ? getSearchTypeTagColor(result.type) : 'blue',
      tagProps: {
        color: result.type ? getSearchTypeTagColor(result.type) : 'blue',
        size: 'xs',
        bordered: false,
        round: true
      },
      ...result
    }
  })
})

// Option renderer function for HLSelect
const optionRenderer = (option) => {
  if (option.renderOption) {
    return option.renderOption()
  }
  return h('span', option.label)
}

// Helper function to get tag color for search type
const getSearchTypeTagColor = (type) => {
  const typeColors = {
    'project': 'blue',
    'feature': 'green', 
    'bug': 'red',
    'testcase': 'purple',
    'testrun': 'orange'
  }
  return typeColors[type?.toLowerCase()] || 'blue'
}

// Helper function to get type label
const getTypeLabel = (type) => {
  return SEARCH_TYPE_LABELS[type] || t('dashboard.unknownType')
}

// Helper function to get status tag config
const getStatusTagConfig = (option) => {
  if (!option.status) return null
  
  const type = option.type?.toLowerCase()
  
  switch (type) {
    case 'project':
      return getProjectStatusHLTag(option.status)
    case 'feature':
      return getFeatureStatusHLTag(option.status)
    case 'bug':
      return getBugStatusHLTag(option.status)
    case 'testcase':
      return getTestCaseStatusHLTag(option.status)
    case 'testrun':
      return getTestRunStatusHLTag(option.status)
    default:
      return null
  }
}

// Map size prop to HLSelect size
const selectSize = computed(() => {
  const sizeMap = {
    sm: 'xs',
    md: 'md',
    lg: 'lg'
  }
  return sizeMap[props.size] || 'md'
})

// Watch modelValue prop changes
watch(() => props.modelValue, (newValue) => {
  searchQuery.value = newValue
  // Sync with HLSelect if it's just a search query (not a selected value)
  if (!selectedValue.value && newValue) {
    // Keep searchQuery in sync but don't change selectedValue
    // HLSelect will handle the search input separately
  }
})

// Watch results to stop loading when they arrive
watch(() => props.results, () => {
  isSearching.value = false
})

// Methods
const handleSearchInput = (searchValue) => {
  // Update search query
  searchQuery.value = searchValue || ''
  emit('update:modelValue', searchQuery.value)
  
  // Clear existing timer
  if (debounceTimer.value) {
    clearTimeout(debounceTimer.value)
  }
  
  // Show loading state if there's a query
  if (searchQuery.value && searchQuery.value.trim()) {
    isSearching.value = true
  } else {
    isSearching.value = false
    // Clear selection when search is cleared
    selectedValue.value = null
  }
  
  // Set new timer for debounced search
  debounceTimer.value = setTimeout(() => {
    if (searchQuery.value.trim()) {
      emit('search', searchQuery.value, selectedFilter.value)
    }
    // Loading will be turned off by the parent when results update
  }, props.debounce)
}

const handleSelectChange = (value, option) => {
  selectedValue.value = value
  // Update search query to match selected option's label
  if (option && option.label) {
    searchQuery.value = option.label
    emit('update:modelValue', option.label)
  }
  // Close dropdown after selection
  closeDropdown()
  // Emit result click event
  if (option) {
    emit('result-click', option)
  }
}

const handleDropdownToggle = (isOpen) => {
  // Don't update if we're programmatically closing the dropdown
  if (isClosingDropdown.value && !isOpen) {
    isClosingDropdown.value = false
    return
  }
  showDropdown.value = isOpen
}

const handleFocus = () => {
  emit('focus')
  if (props.showDropdownOnFocus) {
    // Show dropdown if there's a search query OR if we want to show all results initially
    if (searchQuery.value.trim() || props.showAllResultsInitially) {
      showDropdown.value = true
    }
  } else if (props.showAllResultsInitially) {
    // Even if showDropdownOnFocus is false, show dropdown if showAllResultsInitially is true
    showDropdown.value = true
  }
}

const handleBlur = () => {
  emit('blur')
  // Delay closing to allow clicking on results
  setTimeout(() => {
    closeDropdown()
  }, 200)
}

const clearSearch = () => {
  searchQuery.value = ''
  selectedValue.value = null
  emit('update:modelValue', '')
  emit('clear')
  closeDropdown()
  isSearching.value = false
  // Reset filter to first option (usually "All")
  selectedFilter.value = props.filterOptions[0]?.value || ''
  emit('filter-change', selectedFilter.value)
  
  // Clear HLSelect value and blur
  if (selectRef.value) {
    // Reset HLSelect to show placeholder
    selectRef.value.blur()
  }
}

const handleFilterChange = (value) => {
  selectedFilter.value = value || ''
  emit('filter-change', selectedFilter.value)
  if (searchQuery.value.trim()) {
    emit('search', searchQuery.value, selectedFilter.value)
  }
}

const handleResultClick = (result) => {
  emit('result-click', result)
  closeDropdown()
}

const closeDropdown = () => {
  isClosingDropdown.value = true
  showDropdown.value = false
}

// Click outside handler
const handleClickOutside = (event) => {
  if (searchRef.value && !searchRef.value.contains(event.target)) {
    showDropdown.value = false
  }
}

// Watchers
watch(() => props.results, (newResults) => {
  // Show dropdown when results are available and showAllResultsInitially is enabled
  if (props.showAllResultsInitially && newResults && newResults.length > 0) {
    showDropdown.value = true
  }
}, { immediate: true })

// Watch for showAllResultsInitially prop changes
watch(() => props.showAllResultsInitially, (shouldShow) => {
  if (shouldShow && props.results && props.results.length > 0) {
    showDropdown.value = true
  }
}, { immediate: true })

// Lifecycle
onMounted(() => {
  document.addEventListener('click', handleClickOutside)
  
  // Show dropdown on mount if showAllResultsInitially is enabled and results are available
  if (props.showAllResultsInitially && props.results && props.results.length > 0) {
    showDropdown.value = true
  }
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
  if (debounceTimer.value) {
    clearTimeout(debounceTimer.value)
  }
})

// Expose methods
defineExpose({
  clear: clearSearch,
  focus: () => selectRef.value?.focus(),
  close: closeDropdown
})
</script>

<style scoped>
/* Reduce placeholder text size */
:deep(.search-bar-select .hr-base-selection-placeholder) {
  font-size: 0.75rem !important; /* 12px */
}

:deep(.search-bar-select .hr-base-selection-overlay__wrapper) {
  font-size: 0.75rem !important; /* 12px */
}

:deep(.search-bar-select input::placeholder) {
  font-size: 0.75rem !important; /* 12px */
}

/* Center align filter dropdown text */
:deep(.search-filter-select .hr-base-selection-label) {
  text-align: center !important;
  justify-content: center !important;
}

:deep(.search-filter-select .hr-base-selection-input__content) {
  text-align: center !important;
}

/* Always show dropdown arrows on all screen sizes */
:deep(.search-bar-select .hr-base-suffix),
:deep(.search-filter-select .hr-base-suffix) {
  display: flex !important;
  opacity: 1 !important;
  visibility: visible !important;
}

:deep(.search-bar-select .hr-base-selection-arrow),
:deep(.search-filter-select .hr-base-selection-arrow) {
  display: flex !important;
  opacity: 1 !important;
  visibility: visible !important;
}

:deep(.search-bar-select .hr-base-clear),
:deep(.search-filter-select .hr-base-clear) {
  display: flex !important;
}
</style>

